import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    ime: '',
    priimek: '',
    podjetje: '',
    email: '',
    telefon: '',
    opis: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validacija
    if (!formData.ime || !formData.priimek || !formData.email || !formData.opis) {
      alert('Prosimo, izpolnite vsa obvezna polja.');
      return;
    }

    if (formData.opis.length > 500) {
      alert('Opis ne sme presegati 500 znakov.');
      return;
    }

    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4oa3r9hk4g2rg9v0glg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({
          ime: '',
          priimek: '',
          podjetje: '',
          email: '',
          telefon: '',
          opis: ''
        });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tjasabarbo.com';

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Tjaša Barbo",
    "jobTitle": "Fotografinja in Umetnica",
    "url": siteUrl,
    "image": `${siteUrl}/profile.jpg`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Kočna 24a",
      "addressLocality": "Blejska Dobrava",
      "postalCode": "4273",
      "addressCountry": "SI"
    },
    "email": "tjaska.barbo@gmail.com",
    "telephone": "+386-40-382-292",
    "sameAs": [
      "https://instagram.com/tjasabarbo",
      "https://facebook.com/tjasabarbo",
      "https://behance.net/tjasabarbo",
      "https://vimeo.com/barbofilms"
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Tjaša Barbo",
    "url": siteUrl,
    "description": "Profesionalna fotografinja in vizualna umetnica iz Ljubljane",
    "inLanguage": "sl"
  };

  return (
    <>
      <div className="min-h-screen bg-stone-50">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Link to="/" className="text-sm font-light hover:text-teal-600 transition-colors">Domov</Link>
              <a href="#o-meni" className="text-sm font-light hover:text-teal-600 transition-colors">O meni</a>
              <a href="#portfelj" className="text-sm font-light hover:text-teal-600 transition-colors">Portfelj</a>
            </div>
            
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 font-serif text-xl tracking-wider">
              TJAŠA BARBO
            </Link>
            
            <div className="flex items-center gap-6">
              <button className="w-5 h-5 flex items-center justify-center hover:text-teal-600 transition-colors cursor-pointer">
                <i className="ri-search-line text-lg"></i>
              </button>
              <a href="#kontakt" className="text-sm font-light hover:text-teal-600 transition-colors">Kontakt</a>
              <a href="#razstave" className="bg-black text-white px-6 py-2 rounded-full text-sm font-light hover:bg-gray-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Razstave
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative h-screen flex items-center">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/0d1133c5db3a941e93cf1ec8c83fffb4.jpg" 
              alt="Hero background"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-2xl">
              <h1 className="text-white mb-6">
                <div className="text-7xl font-bold mb-2">TJAŠA BARBO</div>
                <div className="text-5xl font-light mb-2">Fotografinja &</div>
                <div className="text-5xl font-serif italic">Umetnica</div>
              </h1>
              <p className="text-white/80 text-lg mb-12 max-w-xl font-light">
                Raziskovanje svetlobe, trenutkov in umetniških izrazov skozi objektiv
              </p>
              <a href="#portfelj" className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Raziskuj delo
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="o-meni" className="py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex gap-16 items-start">
              <div className="w-2/5">
                <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-6 font-light tracking-wider">
                  UMETNIŠKA POT
                </div>
                <h2 className="mb-8">
                  <div className="text-5xl font-bold mb-2">Življenje skozi</div>
                  <div className="text-5xl font-light text-teal-600">objektiv 📸</div>
                </h2>
                <p className="text-gray-700 leading-relaxed mb-8 text-base font-light">
                  Tjaša Barbo je fotografinja in vizualna umetnica, ki že več kot 15 let raziskuje meje fotografskega medija. Njena dela združujejo dokumentarni pristop s konceptualno umetnostjo, pri čemer se osredotoča na človeške zgodbe, naravne pojave in abstraktne kompozicije.
                  <br /><br />
                  Diplomirala je na Akademiji za likovno umetnost in oblikovanje v Ljubljani, kjer je razvila svoj prepoznavni stil, ki ga zaznamujejo igra svetlobe, globina in čustvena intenzivnost. Njena dela so bila razstavljena v številnih galerijah po Sloveniji in tujini.
                </p>
                <a href="#kontakt" className="text-sm underline hover:text-teal-600 transition-colors cursor-pointer font-light">
                  Preberi celotno zgodbo
                </a>
              </div>
              
              <div className="w-3/5 relative">
                <div className="absolute top-0 right-0 text-right">
                  <div className="mb-8">
                    <div className="text-6xl font-bold">15+</div>
                    <div className="text-sm text-gray-500 font-light">Let izkušenj</div>
                  </div>
                  <div>
                    <div className="text-6xl font-bold">200+</div>
                    <div className="text-sm text-gray-500 font-light">Projektov</div>
                  </div>
                </div>
                
                <div className="relative mt-32">
                  <div className="w-64 h-64 rounded-3xl overflow-hidden shadow-lg">
                    <img 
                      src="https://static.readdy.ai/image/74d546f42f20cb7fa0fe44aca162f085/f067e8d7a84408fee3d68fb37903fd8d.jpeg" 
                      alt="Tjaša Barbo"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  
                  <div className="absolute bottom-0 right-0 w-80 h-96 rounded-3xl overflow-hidden shadow-lg">
                    <img 
                      src="https://readdy.ai/api/search-image?query=photographer%20artist%20working%20in%20studio%20with%20camera%20equipment%20creative%20workspace%20natural%20light%20artistic%20process%20behind%20the%20scenes%20professional%20photography%20setup%20warm%20neutral%20tones&width=500&height=600&seq=about2&orientation=portrait" 
                      alt="Pri delu"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section id="portfelj" className="py-32 bg-stone-100">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-center mb-16">
              <span className="text-6xl font-bold">Filmski </span>
              <span className="text-6xl font-bold text-teal-500">portfelj</span>
            </h2>
            
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Video 1 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/1086934258" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/2018814999-7252b7b717f2ab7962ec9e768ef81be1b70691354551deb6180049623b060295-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/2018814999-7252b7b717f2ab7962ec9e768ef81be1b70691354551deb6180049623b060295-d_960x540?&r=pad&region=us 2x"
                    alt="BONFIRE JAMS: Hana Jerončič - Cimet in med"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">BONFIRE JAMS: Hana Jerončič - Cimet in med</h3>
                      <p className="text-white/80 text-sm font-light">03:07</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 2 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/1061837152" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1988642521-8b3593cafda2ac5d00e5bdabbd131dd1ecfd43837b5e63a4abfafb7b59e77022-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1988642521-8b3593cafda2ac5d00e5bdabbd131dd1ecfd43837b5e63a4abfafb7b59e77022-d_960x540?&r=pad&region=us 2x"
                    alt="Odhajam"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">Odhajam</h3>
                      <p className="text-white/80 text-sm font-light">03:12</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 3 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/1045789841" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1969431483-56647f9678b4965f7bccdd379067073e66fa16640a6f8236fa36b0b950edef1f-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1969431483-56647f9678b4965f7bccdd379067073e66fa16640a6f8236fa36b0b950edef1f-d_960x540?&r=pad&region=us 2x"
                    alt="Gaja Prestor - VODA"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">Gaja Prestor - VODA</h3>
                      <p className="text-white/80 text-sm font-light">02:44</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 4 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/1041048640" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1963737660-6d9cfb8a3b92737650069688f0e76d123bd4f458661422fe2b0cd7ae8bfafc88-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1963737660-6d9cfb8a3b92737650069688f0e76d123bd4f458661422fe2b0cd7ae8bfafc88-d_960x540?&r=pad&region=us 2x"
                    alt="BARBO FILMS 2024"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">BARBO FILMS 2024</h3>
                      <p className="text-white/80 text-sm font-light">00:27</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 5 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/1023048824" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1942123060-1ecee017f1e2fcbdebf62e2f7690c8e94f41781c9c94e3457b690603ce0e3f8a-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1942123060-1ecee017f1e2fcbdebf62e2f7690c8e94f41781c9c94e3457b690603ce0e3f8a-d_960x540?&r=pad&region=us 2x"
                    alt="ZHA - PRINC NA BELEM KONJU"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">ZHA - PRINC NA BELEM KONJU</h3>
                      <p className="text-white/80 text-sm font-light">06:17</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 6 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/1002490527" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1918832250-ce88c98f3e9c5217467f1f6b1770db56d42656c4d466a3030f75d618b5c5bbe5-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1918832250-ce88c98f3e9c5217467f1f6b1770db56d42656c4d466a3030f75d618b5c5bbe5-d_960x540?&r=pad&region=us 2x"
                    alt="Barbo films short video"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">Barbo films short video</h3>
                      <p className="text-white/80 text-sm font-light">00:32</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 7 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/959828083" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1872266707-e9308b4397f825aabb128386a29c4c2452e6871e2af5ec8a1e1ad0086bb28e18-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1872266707-e9308b4397f825aabb128386a29c4c2452e6871e2af5ec8a1e1ad0086bb28e18-d_960x540?&r=pad&region=us 2x"
                    alt="ESPRESSO MARTINI"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">ESPRESSO MARTINI</h3>
                      <p className="text-white/80 text-sm font-light">02:53</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 8 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/959819463" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1872257121-3ff07e03499b0d90979910b70d22f248528dfa91faa9a093d31a68515e6b81ca-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1872257121-3ff07e03499b0d90979910b70d22f248528dfa91faa9a093d31a68515e6b81ca-d_960x540?&r=pad&region=us 2x"
                    alt="Gaja - BISERI"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">Gaja - BISERI</h3>
                      <p className="text-white/80 text-sm font-light">02:55</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 9 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/910139988" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1794001217-34576c3928fa41c444bf42bc588972eade4a3bf4f54b84aa5b98a397218ab2de-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1794001217-34576c3928fa41c444bf42bc588972eade4a3bf4f54b84aa5b98a397218ab2de-d_960x540?&r=pad&region=us 2x"
                    alt="Raiven - Veronika (ESC 2024)"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">Raiven - Veronika (ESC 2024)</h3>
                      <p className="text-white/80 text-sm font-light">02:46</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Video 10 */}
              <div className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer bg-black">
                <a href="https://vimeo.com/895566638" target="_blank" rel="noopener noreferrer" className="block">
                  <img 
                    src="https://i.vimeocdn.com/video/1770337325-a66349afed645374678d4b2531d61857b0b5ee5419917396830e302b6ec36ef8-d_640x360?&r=pad&region=us" 
                    srcSet="https://i.vimeocdn.com/video/1770337325-a66349afed645374678d4b2531d61857b0b5ee5419917396830e302b6ec36ef8-d_960x540?&r=pad&region=us 2x"
                    alt="Teodore - ON"
                    className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/90 rounded-full group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-3xl text-black"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-black/70 backdrop-blur-sm px-6 py-4 rounded-2xl">
                      <h3 className="text-white text-xl font-light mb-1">Teodore - ON</h3>
                      <p className="text-white/80 text-sm font-light">04:07</p>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <div className="text-center">
              <a href="https://vimeo.com/barbofilms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                Oglej si vse filme na Vimeo
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projekti" className="py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <p className="text-sm font-serif text-gray-600 mb-3 font-light tracking-wider">IZBRANI PROJEKTI</p>
              <h2>
                <div className="text-5xl font-bold font-serif">Umetniški</div>
                <div className="text-5xl font-serif italic text-gray-700">projekti</div>
              </h2>
            </div>
            
            <div className="grid grid-cols-4 gap-6">
              <div className="bg-rose-50 rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
                <div className="h-80">
                  <img 
                    src="https://readdy.ai/api/search-image?query=intimate%20portrait%20photography%20soft%20pink%20tones%20emotional%20expression%20fine%20art%20style%20delicate%20lighting%20contemporary%20portraiture%20artistic%20composition%20gentle%20atmosphere%20professional%20work&width=400&height=500&seq=proj1&orientation=portrait" 
                    alt="Projekt 1"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-light mb-2">Intimni portreti</h3>
                  <p className="text-sm text-gray-600 mb-2 font-light">Raziskovanje ranljivosti in moči</p>
                  <p className="text-xs text-gray-500 font-light">2024</p>
                </div>
              </div>
              
              <div className="bg-teal-50 rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
                <div className="h-80">
                  <img 
                    src="https://readdy.ai/api/search-image?query=nature%20landscape%20photography%20mint%20green%20tones%20serene%20environment%20artistic%20perspective%20natural%20beauty%20contemporary%20style%20peaceful%20atmosphere%20minimalist%20composition%20soft%20colors&width=400&height=500&seq=proj2&orientation=portrait" 
                    alt="Projekt 2"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-light mb-2">Zeleni horizonti</h3>
                  <p className="text-sm text-gray-600 mb-2 font-light">Narava kot vir navdiha</p>
                  <p className="text-xs text-gray-500 font-light">2023</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
                <div className="h-80">
                  <img 
                    src="https://readdy.ai/api/search-image?query=black%20and%20white%20architectural%20photography%20geometric%20shapes%20modern%20design%20minimalist%20aesthetic%20strong%20contrast%20contemporary%20art%20clean%20lines%20abstract%20composition%20professional%20quality&width=400&height=500&seq=proj3&orientation=portrait" 
                    alt="Projekt 3"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-light mb-2">Geometrija prostora</h3>
                  <p className="text-sm text-gray-600 mb-2 font-light">Arhitektura in svetloba</p>
                  <p className="text-xs text-gray-500 font-light">2023</p>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
                <div className="h-80">
                  <img 
                    src="https://readdy.ai/api/search-image?query=warm%20golden%20hour%20photography%20sunset%20tones%20emotional%20atmosphere%20artistic%20storytelling%20natural%20light%20contemporary%20fine%20art%20dreamy%20aesthetic%20soft%20focus%20professional%20composition&width=400&height=500&seq=proj4&orientation=portrait" 
                    alt="Projekt 4"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-light mb-2">Zlata ura</h3>
                  <p className="text-sm text-gray-600 mb-2 font-light">Čarobnost naravne svetlobe</p>
                  <p className="text-xs text-gray-500 font-light">2022</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Exhibitions Section */}
        <section id="razstave" className="py-32 bg-stone-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-4">
                <i className="ri-sparkling-line text-blue-600"></i>
                <span className="text-sm text-blue-600 font-light">AKTUALNO</span>
              </div>
              <h2 className="text-5xl font-bold text-gray-900">Razstave in dogodki</h2>
            </div>
            
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-3xl p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-light mb-3">Svetloba in senca</h3>
                <p className="text-sm text-gray-600 mb-4 font-light">15. januar - 28. februar 2025</p>
                <p className="text-sm text-gray-700 mb-4 font-light">Galerija Photon, Ljubljana</p>
                <p className="text-sm text-gray-600 font-light leading-relaxed">
                  Samostojna razstava, ki raziskuje kontraste med svetlobo in senco v urbanem okolju.
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-3xl p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-light mb-3">Portretna serija 2024</h3>
                <p className="text-sm text-gray-600 mb-4 font-light">5. marec - 20. april 2025</p>
                <p className="text-sm text-gray-700 mb-4 font-light">Moderna galerija, Maribor</p>
                <p className="text-sm text-gray-600 font-light leading-relaxed">
                  Skupinska razstava sodobnih slovenskih fotografov s poudarkom na portretni fotografiji.
                </p>
              </div>
              
              <div className="relative rounded-3xl overflow-hidden h-80 group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=art%20gallery%20exhibition%20space%20with%20photographs%20on%20walls%20contemporary%20art%20display%20professional%20lighting%20museum%20interior%20modern%20gallery%20setting%20white%20walls%20artistic%20presentation&width=500&height=600&seq=exh1&orientation=portrait" 
                  alt="Razstava"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-light mb-2">Narava v abstraktnem</h3>
                  <p className="text-sm font-light">Galerija Equrna, Koper</p>
                </div>
              </div>
              
              <div className="col-span-2 relative rounded-3xl overflow-hidden h-80 group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=large%20format%20photography%20exhibition%20gallery%20space%20artistic%20display%20contemporary%20art%20museum%20interior%20professional%20presentation%20modern%20gallery%20walls%20dramatic%20lighting%20fine%20art%20photography&width=900&height=500&seq=exh2&orientation=landscape" 
                  alt="Velika razstava"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-transparent"></div>
                <div className="absolute bottom-8 left-8 text-white max-w-xl">
                  <h3 className="text-3xl font-light mb-3">Retrospektiva: 15 let ustvarjanja</h3>
                  <p className="text-sm font-light mb-2">10. maj - 30. junij 2025</p>
                  <p className="text-base font-light leading-relaxed">
                    Obsežna retrospektivna razstava, ki predstavlja najpomembnejša dela iz petnajstletne kariere.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col gap-6">
                <div className="bg-blue-50 rounded-3xl p-6 hover:shadow-lg transition-shadow flex-1">
                  <h3 className="text-lg font-light mb-2">Delavnica portretne fotografije</h3>
                  <p className="text-xs text-gray-600 mb-3 font-light">22. april 2025</p>
                  <p className="text-sm text-gray-600 font-light">
                    Praktična delavnica za začetnike in napredne.
                  </p>
                </div>
                
                <div className="bg-blue-50 rounded-3xl p-6 hover:shadow-lg transition-shadow flex-1">
                  <h3 className="text-lg font-light mb-2">Predavanje: Svetloba v fotografiji</h3>
                  <p className="text-xs text-gray-600 mb-3 font-light">15. maj 2025</p>
                  <p className="text-sm text-gray-600 font-light">
                    Javno predavanje o pomenu svetlobe.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="povprasevanje" className="py-32 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-6 font-light tracking-wider">
                STOPITE V STIK
              </div>
              <h2 className="text-5xl font-bold mb-4">Povpraševalni obrazec</h2>
              <p className="text-gray-600 text-lg font-light">
                Izpolnite obrazec in kmalu se vam bom javila
              </p>
            </div>

            <form 
              id="inquiry-form" 
              data-readdy-form 
              onSubmit={handleSubmit}
              className="bg-stone-50 rounded-3xl p-12 shadow-lg"
            >
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="ime" className="block text-sm font-light text-gray-700 mb-2">
                    Ime <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="ime"
                    name="ime"
                    value={formData.ime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                    placeholder="Vaše ime"
                  />
                </div>

                <div>
                  <label htmlFor="priimek" className="block text-sm font-light text-gray-700 mb-2">
                    Priimek <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="priimek"
                    name="priimek"
                    value={formData.priimek}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                    placeholder="Vaš priimek"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="podjetje" className="block text-sm font-light text-gray-700 mb-2">
                  Podjetje
                </label>
                <input
                  type="text"
                  id="podjetje"
                  name="podjetje"
                  value={formData.podjetje}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                  placeholder="Ime podjetja (neobvezno)"
                />
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-light text-gray-700 mb-2">
                    E-pošta <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                    placeholder="vas.email@primer.si"
                  />
                </div>

                <div>
                  <label htmlFor="telefon" className="block text-sm font-light text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    value={formData.telefon}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                    placeholder="040 123 456"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="opis" className="block text-sm font-light text-gray-700 mb-2">
                  Opis želja <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="opis"
                  name="opis"
                  value={formData.opis}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm resize-none"
                  placeholder="Opišite vaše želje in potrebe..."
                />
                <div className="text-right text-xs text-gray-500 mt-1 font-light">
                  {formData.opis.length}/500 znakov
                </div>
              </div>

              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-light">
                  ✓ Hvala! Vaše sporočilo je bilo uspešno poslano. Kmalu se vam bom javila.
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-light">
                  ✗ Prišlo je do napake. Prosimo, poskusite znova.
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-black text-white px-8 py-4 rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center gap-3 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Pošiljam...
                  </>
                ) : (
                  <>
                    Pošlji povpraševanje
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </section>

        {/* Contact CTA Section */}
        <section id="kontakt" className="relative py-40">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=photographer%20working%20with%20camera%20artistic%20workspace%20warm%20golden%20lighting%20creative%20atmosphere%20professional%20photography%20equipment%20studio%20setting%20soft%20focus%20background%20inspiring%20environment&width=1920&height=800&seq=cta1&orientation=landscape" 
              alt="Kontakt ozadje"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          
          <div className="relative text-center">
            <h2 className="text-7xl font-bold text-white mb-6 tracking-wider">POVEŽIMO SE</h2>
            <p className="text-white text-xl mb-12 font-light">
              Zanima vas sodelovanje ali imate vprašanja?
            </p>
            <a href="mailto:tjaska.barbo@gmail.com" className="inline-flex items-center gap-4 bg-black text-white px-8 py-4 rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-full">
                <i className="ri-mail-line text-white"></i>
              </span>
              <span className="tracking-wider">KONTAKTIRAJ ME</span>
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-start mb-16">
              <div className="w-1/3">
                <h3 className="text-4xl font-bold mb-6">TJAŠA BARBO</h3>
                <p className="text-white/80 text-base mb-8 font-light leading-relaxed">
                  Fotografinja in umetnica, ki raziskuje meje vizualnega pripovedovanja zgodb.
                </p>
                <div className="flex gap-4">
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-full hover:bg-white hover:text-black transition-colors cursor-pointer">
                    <i className="ri-instagram-line"></i>
                  </a>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-full hover:bg-white hover:text-black transition-colors cursor-pointer">
                    <i className="ri-facebook-line"></i>
                  </a>
                  <a href="https://behance.net" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-full hover:bg-white hover:text-black transition-colors cursor-pointer">
                    <i className="ri-behance-line"></i>
                  </a>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-full hover:bg-white hover:text-black transition-colors cursor-pointer">
                    <i className="ri-linkedin-line"></i>
                  </a>
                </div>
              </div>
              
              <div className="flex gap-16">
                <div>
                  <h4 className="text-xs tracking-wider mb-4 font-light">NASLOV</h4>
                  <p className="text-white/80 text-sm font-light">Kočna 24a</p>
                  <p className="text-white/80 text-sm font-light">4273 Blejska Dobrava</p>
                </div>
                
                <div>
                  <h4 className="text-xs tracking-wider mb-4 font-light">TELEFON</h4>
                  <p className="text-white/80 text-sm font-light">040 382 292</p>
                </div>
                
                <div>
                  <h4 className="text-xs tracking-wider mb-4 font-light">E-POŠTA</h4>
                  <p className="text-white/80 text-sm font-light">tjaska.barbo@gmail.com</p>
                </div>
              </div>
              
              <div className="w-48 h-64 rounded-2xl overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/74d546f42f20cb7fa0fe44aca162f085/f067e8d7a84408fee3d68fb37903fd8d.jpeg" 
                  alt="Tjaša Barbo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div className="border-t border-white/10 pt-8 flex justify-between items-center">
              <p className="text-white/60 text-sm font-light">© 2025 Tjaša Barbo. Vse pravice pridržane.</p>
              <div className="flex gap-8">
                <a href="#" className="text-white/60 text-sm hover:text-white transition-colors font-light cursor-pointer">Politika zasebnosti</a>
                <a href="#" className="text-white/60 text-sm hover:text-white transition-colors font-light cursor-pointer">Pogoji uporabe</a>
                <a href="#" className="text-white/60 text-sm hover:text-white transition-colors font-light cursor-pointer">Piškotki</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
